import React, { useLayoutEffect, useRef, useState, useCallback } from "react";
import { Check } from "lucide-react";

/**
 * Node-graph canvas with REAL connection ports.
 * Each node renders an input port (left) and output port (right); connectors are
 * bezier paths measured from the actual port DOM positions, so they always attach
 * exactly — at any size. Recomputes on resize / font settle.
 *
 * nodes: [{ id, x, y, icon, title, sub }]   x,y = CENTER position in % of canvas
 * edges: [{ from, to, dim }]                from.out-port -> to.in-port
 */
export default function PipelineCanvas({ nodes, edges, ariaLabel, className = "" }) {
  const wrapRef = useRef(null);
  const portRefs = useRef({});
  const [paths, setPaths] = useState([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const wr = wrap.getBoundingClientRect();
    if (!wr.width) return;
    setSize({ w: wr.width, h: wr.height });
    const center = (el) => {
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2 - wr.left, y: r.top + r.height / 2 - wr.top };
    };
    const next = edges
      .map((e) => {
        const a = portRefs.current[e.from + ":out"];
        const b = portRefs.current[e.to + ":in"];
        if (!a || !b) return null;
        const s = center(a), t = center(b);
        // Control-handle length. MUST stay under half the horizontal gap, else the
        // outgoing handle overshoots the incoming one (s.x+dx > t.x-dx) and the
        // bezier wiggles — which is what made the fan lines cross/tangle. Clamp to
        // <= 0.5*gap so every connector is a clean, monotone S-curve.
        const gap = Math.abs(t.x - s.x);
        const dx = Math.min(gap * 0.5, Math.max(24, gap * 0.42));
        return { d: `M ${s.x} ${s.y} C ${s.x + dx} ${s.y}, ${t.x - dx} ${t.y}, ${t.x} ${t.y}`, dim: !!e.dim };
      })
      .filter(Boolean);
    setPaths(next);
  }, [edges]);

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(recompute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", recompute);
    const t1 = setTimeout(recompute, 200);
    const t2 = setTimeout(recompute, 600); // after webfonts settle
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
      clearTimeout(t1); clearTimeout(t2);
    };
  }, [recompute, nodes]);

  // Only render a port an edge actually attaches to. Hiding the unused
  // Target-in / Report-out stubs stops them poking outside the card, so the
  // nodes can sit wider apart and the connectors get real horizontal room.
  const usedPorts = new Set();
  (edges || []).forEach((e) => { usedPorts.add(e.from + ":out"); usedPorts.add(e.to + ":in"); });

  return (
    <div className={`au-canvas au-pipeline ${className}`} ref={wrapRef} role="img" aria-label={ariaLabel}>
      <svg
        className="au-connectors"
        width={size.w || 1}
        height={size.h || 1}
        viewBox={`0 0 ${size.w || 1} ${size.h || 1}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {paths.map((p, i) => (
          <path key={i} d={p.d} className={p.dim ? "au-dim" : ""} />
        ))}
      </svg>

      {nodes.map((n) => (
        <div
          key={n.id}
          className="au-node au-np"
          style={{ position: "absolute", left: n.x + "%", top: n.y + "%", transform: "translate(-50%, -50%)" }}
        >
          {usedPorts.has(n.id + ":in") && (
            <span className="au-port au-port-in" ref={(el) => { portRefs.current[n.id + ":in"] = el; }} aria-hidden="true" />
          )}
          <span className="au-node-ico" aria-hidden="true">{n.icon}</span>
          <div>
            <div className="au-node-title">{n.title}</div>
            {n.sub && <div className="au-node-sub">{n.sub}</div>}
          </div>
          <Check className="au-check" aria-hidden="true" />
          {usedPorts.has(n.id + ":out") && (
            <span className="au-port au-port-out" ref={(el) => { portRefs.current[n.id + ":out"] = el; }} aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  );
}
